import { useThree } from '@react-three/fiber';
import {
    useEffect,
    useLayoutEffect,
} from 'react';
import { getShadowCacheSize } from './cache';
import type { ActiveShadow } from './useBakedShadow';
import type {
    ShadowModel,
} from './types';
import { renderReadiness } from '../Rendering/browserReadiness';

/**
 * Publishes the shadow revision only after the cached receiver has crossed two
 * demand-loop frames. The Puppeteer renderbot treats this acknowledgement as
 * a screenshot barrier, so publishing directly after the bake is too early.
 */
export default function useShadowReadiness(
    model:ShadowModel|undefined,
    visibleShadow:ActiveShadow|null,
):void {
    const invalidate = useThree(state => state.invalidate);
    const cacheKey = model?.cacheKey;
    const modelRevision = model?.modelRevision;

    useLayoutEffect(() => {
        renderReadiness.setModelReady(false);
        if(modelRevision !== undefined) {
            renderReadiness.reset('shadow', modelRevision);
        }
        renderReadiness.setShadowDiagnostics({
            bakeTimeMs: undefined,
            cacheKey,
            cacheSize: getShadowCacheSize(),
            cacheStatus: undefined,
        });
    }, [cacheKey, modelRevision]);

    useEffect(() => {
        if(!model || !visibleShadow) return;

        let cancelled = false;
        let readyFrame = 0;
        invalidate();
        const renderFrame = requestAnimationFrame(() => {
            if(cancelled) return;

            invalidate();
            readyFrame = requestAnimationFrame(() => {
                if(cancelled) return;

                invalidate();
                renderReadiness.setShadowDiagnostics({
                    bakeTimeMs: visibleShadow.bakeTimeMs,
                    cacheKey: model.cacheKey,
                    cacheSize: getShadowCacheSize(),
                    cacheStatus: visibleShadow.status,
                });
                if(model.modelRevision !== undefined) {
                    renderReadiness.acknowledge(
                        'shadow',
                        model.modelRevision,
                    );
                }
                renderReadiness.setModelReady(true);
            });
        });

        return () => {
            cancelled = true;
            cancelAnimationFrame(renderFrame);
            cancelAnimationFrame(readyFrame);
        };
    }, [invalidate, model, visibleShadow]);
}
